import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Package, Plus, AlertTriangle } from "lucide-react";
import { assetApi } from "../api";
import type { AssetListParams } from "../api";
import { useAsyncData, usePagination } from "../hooks";
import {
  ActiveFilterBadges, AssetError, AssetLoading, AssetStatusBadge,
  BackBtn, Btn, ConditionBadge, EmptyState, ExportBtn, FilterPanel,
  PageHeader, Pagination, SearchInput, SelectFilter,
  Table, TBody, TD, TH, THead, TR, fmt,
} from "../components/AssetUI";
import { downloadXls, ASSET_EXPORT_HEADERS, assetToRow } from "../utils/export";
import { useAuth } from "#/context/AuthProvider";

const STATUS_OPTS = [
  { label: "Available",        value: "AVAILABLE"         },
  { label: "Assigned",         value: "ASSIGNED"          },
  { label: "Under Maintenance",value: "UNDER_MAINTENANCE" },
  { label: "Pending Disposal", value: "PENDING_DISPOSAL"  },
  { label: "Disposed",         value: "DISPOSED"          },
  { label: "Lost",             value: "LOST"              },
  { label: "Damaged",          value: "DAMAGED"           },
];

const INIT_FILTERS: AssetListParams = { search: "", status: "", categoryId: undefined };

export default function AssetListPage() {
  const { hasRole, hasAnyPermission } = useAuth();
  const canCreate = hasRole("SUPER_ADMIN") || hasRole("ADMIN") || hasAnyPermission(["ASSET_CREATE", "ASSET_MANAGE"]);
  const pagination = usePagination();
  const [filters, setFilters] = useState<AssetListParams>(INIT_FILTERS);
  const [exporting, setExporting] = useState(false);

  const cats = useAsyncData(() => assetApi.listCategories(), []);

  const { data, loading, error, reload } = useAsyncData(
    () => assetApi.listAssets(pagination.page, pagination.size, filters),
    [pagination.page, pagination.size, filters],
  );

  function setFilter<K extends keyof AssetListParams>(k: K, v: AssetListParams[K]) {
    setFilters((f) => ({ ...f, [k]: v }));
    pagination.reset();
  }
  function clearFilters() { setFilters(INIT_FILTERS); pagination.reset(); }

  const activeBadges = [
    filters.search     && { label: `"${filters.search}"`,       onRemove: () => setFilter("search", "") },
    filters.status     && { label: `Status: ${filters.status}`, onRemove: () => setFilter("status", "") },
    filters.categoryId && { label: `Category: ${cats.data?.find(c => c.id === filters.categoryId)?.name ?? filters.categoryId}`, onRemove: () => setFilter("categoryId", undefined) },
  ].filter(Boolean) as { label: string; onRemove: () => void }[];

  const doExport = useCallback(async () => {
    setExporting(true);
    try {
      await downloadXls(
        `assets-export-${new Date().toISOString().slice(0, 10)}.xls`,
        "Asset Register",
        ASSET_EXPORT_HEADERS,
        (data?.content ?? []).map(assetToRow),
      );
    } finally { setExporting(false); }
  }, [data]);

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Asset Management"
        title="Asset Register"
        actions={
          <div className="flex items-center gap-2">
            <BackBtn to="/assets" label="Dashboard" />
            <ExportBtn onExport={doExport} loading={exporting} />
            {canCreate && (
              <Btn variant="primary" size="md" icon={Plus} onClick={() => {}}>
                <Link to="/assets/new" className="contents">Add Asset</Link>
              </Btn>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={filters.search ?? ""}
          onChange={(v) => setFilter("search", v)}
          placeholder="Search by name, code, or serial…"
          className="min-w-64 flex-1"
        />
        <FilterPanel activeCount={activeBadges.length} onClear={clearFilters} onRefresh={reload}>
          <SelectFilter label="All Statuses" value={filters.status ?? ""} onChange={(v) => setFilter("status", v)}
            options={STATUS_OPTS} />
          <SelectFilter label="All Categories" value={filters.categoryId ? String(filters.categoryId) : ""}
            onChange={(v) => setFilter("categoryId", v ? Number(v) : undefined)}
            options={(cats.data ?? []).map((c) => ({ label: c.name, value: String(c.id) }))} />
        </FilterPanel>
      </div>

      <ActiveFilterBadges filters={activeBadges} />

      {loading && <AssetLoading label="Loading assets…" />}
      {error   && <AssetError message={error} onRetry={reload} />}

      {!loading && data && (
        <>
          <Table>
            <THead>
              <TH>Asset</TH><TH>Category</TH><TH>Location</TH>
              <TH>Status</TH><TH>Condition</TH><TH>Assigned To</TH>
              <TH right>Purchase Cost</TH><TH right>Current Value</TH><TH right>Action</TH>
            </THead>
            <TBody>
              {data.content.length === 0 ? (
                <tr><td colSpan={9}><EmptyState icon={Package} title="No assets found" description="Try adjusting your search or filters." /></td></tr>
              ) : data.content.map((asset) => (
                <TR key={asset.id} className={asset.flaggedForDisposal ? "bg-red-50/30" : ""}>
                  <TD>
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-xs font-bold text-indigo-700">
                        {asset.categoryName.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-800">{asset.name}</div>
                        <div className="font-mono text-xs text-slate-400">{asset.assetNumber}</div>
                      </div>
                    </div>
                  </TD>
                  <TD muted>{asset.categoryName}</TD>
                  <TD muted>{asset.locationName ?? "—"}</TD>
                  <TD><AssetStatusBadge value={asset.status} /></TD>
                  <TD><ConditionBadge value={asset.condition} /></TD>
                  <TD muted>{asset.assignedEmployeeName ?? "—"}</TD>
                  <TD right muted>{fmt(asset.purchaseCost)}</TD>
                  <TD right muted>{fmt(asset.currentValue)}</TD>
                  <TD right>
                    <div className="flex items-center justify-end gap-1.5">
                      {asset.flaggedForDisposal && (
                        <span title="Flagged for disposal"><AlertTriangle className="h-4 w-4 text-red-400" /></span>
                      )}
                      <Link to={`/assets/${asset.id}`}
                        className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition">
                        View
                      </Link>
                      <Link to={`/assets/${asset.id}/edit`}
                        className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 transition">
                        Edit
                      </Link>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          <Pagination page={data} onPageChange={pagination.setPage} onSizeChange={pagination.setSize} />
        </>
      )}
    </div>
  );
}
